import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/chat`;

const QUICK_REPLIES = [
  'What programmes do you offer?',
  'How can I donate?',
  'How do I volunteer?',
  'I need free counselling',
  'Where are your branches?',
];

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Boot session lazily when the widget opens for the first time
  useEffect(() => {
    if (!isOpen || sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/new`, { method: 'POST' });
        const data = await res.json();
        if (cancelled) return;
        setSessionId(data.session_id);
        setMessages([{ type: 'bot', text: data.reply }]);
      } catch {
        if (cancelled) return;
        setMessages([{ type: 'bot', text: "Hi! I'm the EWRF Assistant. How can I help?" }]);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, sessionId]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const handleSend = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || sending) return;
    setMessages((prev) => [...prev, { type: 'user', text: trimmed }]);
    setMessage('');
    setSending(true);
    try {
      const res = await fetch(`${API}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: trimmed, session_id: sessionId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'chat failed');
      if (data.session_id && !sessionId) setSessionId(data.session_id);
      setMessages((prev) => [...prev, { type: 'bot', text: data.reply }]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { type: 'bot', text: "Sorry — I'm having trouble reaching the server. Please email admin@ewrf.org.my or call +603 7498 7140." },
      ]);
    } finally {
      setSending(false);
    }
  };

  const renderMarkdownLite = (text) => {
    // Very small subset: bold + line breaks
    const withBold = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    return { __html: withBold.replace(/\n/g, '<br/>') };
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 md:bottom-8 right-4 md:right-8 w-14 h-14 md:w-16 md:h-16 bg-[#D4AF37] rounded-full shadow-2xl flex items-center justify-center text-white transition-all duration-300 hover:scale-110 z-50"
        data-testid="chat-toggle-btn"
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? <X className="w-5 h-5 md:w-6 md:h-6" /> : (
          <div className="relative">
            <MessageCircle className="w-5 h-5 md:w-6 md:h-6" />
            <span className="absolute -top-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-[#FF6B6B] rounded-full flex items-center justify-center text-[9px] md:text-[10px] font-bold">AI</span>
          </div>
        )}
      </button>

      {isOpen && (
        <Card className="fixed bottom-20 md:bottom-28 right-4 md:right-8 w-[calc(100vw-2rem)] sm:w-80 md:w-96 shadow-2xl z-50 border-[#D4AF37]/20" data-testid="chat-widget">
          <CardContent className="p-0">
            <div className="bg-gradient-to-r from-[#D4AF37] to-[#C19A2E] p-4 rounded-t-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center p-1">
                  <img
                    src="https://customer-assets.emergentagent.com/job_team-sync-95/artifacts/886a87kg_WhatsApp%20Image%202026-05-19%20at%204.29.59%20PM.jpeg"
                    alt="EWRF Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="min-w-0">
                  <h5 className="font-semibold text-white flex items-center gap-1.5">
                    EWRF Assistant <Sparkles className="w-3.5 h-3.5" />
                  </h5>
                  <p className="text-xs text-white/85">AI-powered · Online</p>
                </div>
              </div>
            </div>

            <div ref={scrollRef} className="h-80 overflow-y-auto p-4 bg-gray-50" data-testid="chat-scroll">
              {messages.map((msg, index) => (
                <div key={index} className={`mb-3 ${msg.type === 'user' ? 'text-right' : 'text-left'}`}>
                  <div
                    className={`inline-block max-w-[85%] p-3 rounded-lg text-sm leading-relaxed ${
                      msg.type === 'user'
                        ? 'bg-[#D4AF37] text-white rounded-br-none'
                        : 'bg-white text-gray-700 shadow-sm rounded-bl-none'
                    }`}
                    dangerouslySetInnerHTML={renderMarkdownLite(msg.text)}
                  />
                </div>
              ))}

              {sending && (
                <div className="text-left mb-3">
                  <div className="inline-block bg-white text-gray-500 shadow-sm rounded-lg rounded-bl-none p-3 text-sm">
                    <span className="inline-flex gap-1">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '120ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '240ms' }}></span>
                    </span>
                  </div>
                </div>
              )}

              {messages.length <= 1 && !sending && (
                <div className="mt-3 space-y-1.5">
                  <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-1">Quick starters</p>
                  {QUICK_REPLIES.map((q) => (
                    <button
                      key={q}
                      onClick={() => handleSend(q)}
                      className="w-full text-left bg-white hover:bg-[#D4AF37]/10 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-700 transition-colors"
                      data-testid={`chat-quickreply-${q.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t">
              <form onSubmit={(e) => { e.preventDefault(); handleSend(message); }} className="flex gap-2" data-testid="chat-form">
                <Input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Ask about programmes, donations..."
                  className="flex-1 border-gray-300"
                  disabled={sending}
                  data-testid="chat-input"
                />
                <Button
                  type="submit"
                  disabled={sending || !message.trim()}
                  className="bg-[#D4AF37] hover:bg-[#C19A2E] text-white disabled:opacity-60"
                  data-testid="chat-send-btn"
                  aria-label="Send message"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default ChatWidget;
